<template>
  <div
    id="store-listing"
    class="store-listing"
  >
    <fn-section
      background-color="white"
      background-color-mobile="sand-light"
      constrained="l"
      padding-bottom="xs"
      padding-top="xs"
    >
      <fn-breadcrumbs :nodes="loadedPage.content.breadcrumbs" />
    </fn-section>

    <fn-cms-section-loader
      v-if="loadedPage.content.content_slots.a && loadedPage.content.content_slots.a.sections"
      :sections="loadedPage.content.content_slots.a.sections"
    />

    <fn-section
      background-color="white"
      constrained="l"
      padding-bottom="s"
      padding-top="xs"
    >
      <fn-headline
        color="grey-dark"
        size="h2"
        type="h1"
      >
        {{ $t('storeListing.title') }}
      </fn-headline>
    </fn-section>

    <fn-section
      class="sl-wrapper"
      no-padding-x
    >
      <fn-section
        class="store-search-section"
        constrained="l"
        no-padding-x
      >
        <div
          :class="{
            'store-search--no-stores': !hasStores,
          }"
          class="store-search"
        >
          <fn-store-search-panel
            id="store-listing-search-panel"
            :errors="errors"
            :filter-properties="filterProperties"
            :loading="loading"
            :search-payload-initial="query"
            @locate-me="handleLocateMeFiltered"
            @search="handleSearchFiltered"
          />
        </div>

        <fn-store-teaser-list
          :stores="storeList.stores"
          class="show-m"
          default-opened
          open-to-top
          @calc-route="handleCalculateRoute"
          @show-details="goToStoreDetailPage"
        />
      </fn-section>

      <div class="map-container">
        <client-only>
          <GmapMap
            ref="map"
            class="map"
            :center="centerCoordinates"
            :styles="mapStyles"
            :zoom="centerCoordinates.zoom"
            :api-key="apiKey"
            disable-default-ui
          >
            <GmapMarker
              v-if="userPosition.lat && userPosition.lng"
              :options="{
                icon: {
                  url: userIconUrl,
                  scaledSize: { width: 28, height: 35 },
                },
                position: userPosition,
              }"
            />

            <GmapCircle
              v-if="userPosition.lat && userPosition.lng"
              :center="userPosition"
              :options="{
                strokeColor: 'transparent',
                strokeOpacity: 0,
                strokeWeight: 0,
                fillColor: '#000000',
                fillOpacity: 0.1,
              }"
              :radius="25000"
            />

            <template v-if="!hasStores && favoriteStore">
              <GmapMarker
                :options="{
                  icon: {
                    url: favoriteIconUrl,
                    scaledSize: mapMarkerSize(favoriteStore.name),
                  },
                  position: {
                    lat: favoriteStore.geoPoint.latitude,
                    lng: favoriteStore.geoPoint.longitude,
                  },
                }"
                @click="() => (infoWindowStore = favoriteStore)"
              />
            </template>

            <template v-if="hasStores">
              <div
                v-for="store in storeList.stores"
                :key="store.name"
              >
                <GmapMarker
                  v-if="favoriteStore && store.name === favoriteStore.name"
                  :options="{
                    icon: {
                      url: favoriteIconUrl,
                      scaledSize: mapMarkerSize(favoriteStore.name),
                    },
                    position: {
                      lat: favoriteStore.geoPoint.latitude,
                      lng: favoriteStore.geoPoint.longitude,
                    },
                  }"
                  @click="() => (infoWindowStore = favoriteStore)"
                />
                <GmapMarker
                  v-else
                  :options="{
                    icon: {
                      url: iconUrl,
                      scaledSize: mapMarkerSize(store.name),
                    },
                    position: {
                      lat: store.geoPoint.latitude,
                      lng: store.geoPoint.longitude,
                    },
                  }"
                  @click="() => (infoWindowStore = store)"
                />
              </div>
            </template>

            <GmapInfoWindow
              v-if="infoWindowStore"
              :options="{
                ...infoWindowOptions,
                position: {
                  lat: infoWindowStore.geoPoint.latitude,
                  lng: infoWindowStore.geoPoint.longitude,
                },
              }"
              opened
            >
              <fn-store-teaser
                :favorite-store="favoriteStore && infoWindowStore.name === favoriteStore.name"
                :store="infoWindowStore"
                class="info-window-content"
                info-window
                @calc-route="handleCalculateRoute(infoWindowStore)"
                @close="infoWindowStore = null"
                @show-details="goToStoreDetailPage(infoWindowStore)"
              />
            </GmapInfoWindow>
          </GmapMap>
        </client-only>
      </div>

      <fn-store-teaser-list
        :stores="storeList.stores"
        class="hide-m"
        @calc-route="handleCalculateRoute"
        @show-details="goToStoreDetailPage"
      />
    </fn-section>

    <fn-cms-section-loader
      v-if="loadedPage.content.content_slots.b && loadedPage.content.content_slots.b.sections"
      :sections="loadedPage.content.content_slots.b.sections"
    />

    <fn-section
      v-if="regionsStoresList && regionsStoresList.length"
      constrained="l"
      padding-top="s"
    >
      <fn-headline
        size="h2"
        type="h2"
      >
        {{ $t('storeListing.seo.title') }}
      </fn-headline>
      <fn-accordion>
        <fn-accordion-item
          v-for="(item, index) of regionsStoresList"
          :id="index"
          :key="item.region"
          headline-type="div"
          :headline="item.region"
        >
          <template #headline>
            {{ item.region }}
          </template>
          <fn-link
            v-for="store of item.stores"
            :key="store.storeNumber"
            :to="`/stores/${store.partForUrlGen}/`"
            class="regions-link"
            color="green-dark"
            large
            no-arrow
            underline
          >
            {{ store.displayName }}
          </fn-link>
        </fn-accordion-item>
      </fn-accordion>
    </fn-section>
  </div>
</template>

<script>
import {
  Circle as GmapCircle,
  InfoWindow as GmapInfoWindow,
  GoogleMap as GmapMap,
  Marker as GmapMarker,
} from 'vue3-google-map';
import GoogleMaps from '@/mixins/GoogleMaps';
import LocalStorage from '@/mixins/LocalStorage';

import ImgLocationFav from '@/assets/img/icon-location-favorite.svg';
import ImgLocationGrey from '@/assets/img/icon-location-grey.svg';

export default {
  name: 'fn-store-listing',
  components: {
    GmapCircle,
    GmapMap,
    GmapMarker,
    GmapInfoWindow,
  },
  mixins: [GoogleMaps, LocalStorage],
  props: {
    page: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      query: this.$route.query.q || '',
      infoWindowStore: null,
      infoWindowOptions: {
        pixelOffset: {
          width: 0,
          height: -50,
        },
      },
      regionsStoresList: [],
    };
  },
  watch: {
    $route(query) {
      this.centerCoordinates = this.defaultCenterCoordinates;
      this.query = query.query.q;

      this.handleSearchFiltered({
        query: query.query.q,
      });
    },
    storeList() {
      this.infoWindowStore = null;
    },
    async infoWindowStore(store) {
      if (!store) {
        return;
      }

      const map = await this.$refs.map.map;

      const storeLatLng = new window.google.maps.LatLng(store.geoPoint.latitude, store.geoPoint.longitude);
      const latLng = new window.google.maps.LatLngBounds();

      latLng.extend(storeLatLng);

      map.panToBounds(latLng, { top: 500, right: 1200, left: 600 });
    },
  },
  computed: {
    ...mapState(useMainStore, {
      baseStore: 'baseStore',
      loadedPage: 'loadedPage',
    }),

    ...mapState(useUserStore, {
      user: 'user',
      isAuthenticated: 'isAuthenticated',
      favoriteStore: 'favoriteStore',
    }),

    favoriteIconUrl() {
      return ImgLocationFav;
    },

    iconUrl() {
      return ImgLocationGrey;
    },
  },
  mounted() {
    this.getRegionsStoresList().then((response) => {
      this.regionsStoresList = response;
    });

    if (this.query) {
      this.handleSearchFiltered({
        query: this.query,
      });
    }
  },
  methods: {
    ...mapActions(useMainStore, {
      getStoreList: 'getStoreList',
      getRegionsStoresList: 'getRegionsStoresList',
    }),

    ...mapActions(useUserStore, {
      setFavoriteStore: 'setFavoriteStore',
      loadUser: 'loadUser',
    }),

    handleCalculateRoute(store) {
      let url = `https://maps.google.com/maps?f=d&daddr=${store.address.line1}${
        store.address.line2 ? `+${store.address.line2}` : ''
      }+${store.address.postalCode}+${store.address.town}`;

      if (this.userPosition.lat && this.userPosition.lng) {
        url += `&saddr=${this.userPosition.lat},${this.userPosition.lng}`;
      }

      window.open(url, '_blank');
    },

    async goToStoreDetailPage(store) {
      await this.$router.push({ path: this.$t('url.stores.detail', { id: store.partForUrlGen }) });
    },

    mapMarkerSize(storeName) {
      if (this.infoWindowStore && storeName === this.infoWindowStore.name) {
        return {
          width: 40,
          height: 50,
        };
      }

      return {
        width: 28,
        height: 35,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.sl-wrapper {
  @include bp(m) {
    border-top: 1px solid palette(grey, lighter);
    border-bottom: 1px solid palette(grey, lighter);
  }
}

.store-search-section {
  @include bp(m) {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }

  :deep(.section-content) {
    height: 100%;
  }
}

.map-container {
  overflow: hidden;
  height: 75vh;
}

.map {
  height: 100%;

  .info-window-content {
    width: 310px;
    min-height: 120px;

    @include bp(m) {
      width: 375px;
    }
  }
}

.store-search {
  @include bp(m) {
    position: absolute;
    top: $pad;
    bottom: $pad;
    left: 0;
    right: auto;
    z-index: 1;
    box-shadow: 0 2px 5px rgba(palette(black), 0.1);
    width: 343px;
  }

  &.store-search--no-stores {
    @include bp(m) {
      bottom: auto;
    }
  }
}

:deep(.gm-style-iw-t) {
  &::after {
    display: none;
  }

  .gm-style-iw {
    display: block;
    padding: 0;
    border-radius: map-get($border-radiuses, 'xxl');
    max-height: unset !important;
    max-width: 310px !important;

    @include bp(m) {
      max-width: 375px !important;
    }

    .gm-style-iw-chr {
      display: none !important;
    }

    .gm-style-iw-d {
      overflow: hidden !important;
      max-height: unset !important;
      visibility: visible;
    }
  }
}

:deep(.gm-style-iw-tc) {
  display: none !important;
}

:deep(.regions-link) {
  display: block;
}
</style>
